import SettingsItem from "@/components/PageEditor/components/Viewport/components/Sidebar/components/Settings/components/SettingsItem";
import SettingsItemRadio from "@/components/PageEditor/components/Viewport/components/Sidebar/components/Settings/components/SettingsItemRadio";
import SettingsItemText from "@/components/PageEditor/components/Viewport/components/Sidebar/components/Settings/components/SettingsItemText";
import SettingsSection from "@/components/PageEditor/components/Viewport/components/Sidebar/components/Settings/components/SettingsSection";
import { PageService } from "@/services/admin-services";
import { Page } from "@/services/admin-services/models";
import { ROOT_NODE, useEditor, useNode } from "@craftjs/core";
import { Grid, MenuItem } from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { NavigationItem } from "./components/TableOfContents/models/NavigationItem";

const toNavigationItem = (page: Page, isCurrent: boolean): NavigationItem => ({
    id: page.id,
    title: page.title,
    to: page.slug,
    type: page?.pageType,
    order: page.parentOrder,
    isCurrent: isCurrent,
    children: [],
});

const Settings = () => {
    const { id } = useNode();
    const { language, displayName, toc, parent, slug, actions } = useEditor((state) => ({
        language: state.nodes[ROOT_NODE].data.props.language,
        displayName:
            state.nodes[id] && state.nodes[id].data.custom.displayName
                ? state.nodes[id].data.custom.displayName
                : state.nodes[id].data.displayName,
        toc: state.nodes[id].data.props.toc,
        parent: state.nodes[ROOT_NODE].data.props.parentId,
        slug: state.nodes[ROOT_NODE].data.props.slug,
    }));

    const pageService = useMemo(() => new PageService("int"), []);

    const [pages, setPages] = useState<Array<Page>>();
    const [rootPage, setRootPage] = useState<NavigationItem | null>(null);

    const { identifier } = useParams();

    useEffect(() => {
        pageService.getPages().then(setPages);
    }, [pageService]);

    useEffect(() => {
        if (!pages || pages.length === 0) return;

        const tocPages = pages.filter((p) => p.language === language);
        let navigation: NavigationItem | null = null;

        let parentId: string | undefined = identifier;
        while (parentId !== undefined) {
            const parentPage = tocPages.find((page) => page.id === parentId);
            parentId = parentPage?.parentPageId;

            if (parentPage) {
                const navigationItem = toNavigationItem(parentPage, parentPage.slug === slug);

                if (navigation) navigationItem.children.push(navigation);
                navigation = navigationItem;
            }
        }

        setRootPage(navigation);
    }, [identifier, language, pages, parent, slug]);

    const renderNavigation = (item: NavigationItem, level: number = 0): ReactNode => {
        if (!rootPage) return null;

        return [
            <MenuItem key={item.id} value={item.id} sx={{ pl: 2 + level * 2 }}>
                {item.title}
            </MenuItem>,
            item.children.map((child) => renderNavigation(child, level + 1)),
        ];
    };

    return (
        <>
            <SettingsSection
                title="Identification"
                summary={() => {
                    return `${displayName || "-"}`;
                }}
            >
                <Grid size={{ xs: 12 }}>
                    <SettingsItemText
                        type={"text"}
                        value={displayName}
                        onChange={(value: string) => {
                            actions.setCustom(id, (custom) => (custom.displayName = value));
                        }}
                        fullWidth={true}
                        label="Display name"
                    />
                </Grid>
            </SettingsSection>
            <SettingsSection title="Settings" summary={() => ""}>
                <Grid size={{ xs: 12 }}>
                    <SettingsItem propKey="filters" type="radio" label="Display filter" defaultValue={"no"}>
                        <SettingsItemRadio value={"yes"} label="Yes" />
                        <SettingsItemRadio value={"no"} label="No" />
                    </SettingsItem>
                    <SettingsItem propKey="anchors" type="radio" label="Display anchors" defaultValue={"no"}>
                        <SettingsItemRadio value={"yes"} label="Yes" />
                        <SettingsItemRadio value={"no"} label="No" />
                    </SettingsItem>
                    <SettingsItem propKey="tags" type="radio" label="Display tags" defaultValue={"no"}>
                        <SettingsItemRadio value={"yes"} label="Yes" />
                        <SettingsItemRadio value={"no"} label="No" />
                    </SettingsItem>
                </Grid>
            </SettingsSection>
            <SettingsSection title="Table of Contents" summary={() => (toc === "yes" ? "Yes" : "No")}>
                <Grid size={{ xs: 12 }}>
                    <SettingsItem propKey="toc" type="radio" label="Enabled?" defaultValue={"no"}>
                        <SettingsItemRadio value={"yes"} label="Yes" />
                        <SettingsItemRadio value={"no"} label="No" />
                    </SettingsItem>
                    <br />
                    <SettingsItem fullWidth propKey="tocParent" type="select" label="Highest level (optional)">
                        <MenuItem value={"all"}>- All -</MenuItem>
                        {rootPage && renderNavigation(rootPage)}
                    </SettingsItem>
                </Grid>
            </SettingsSection>
        </>
    );
};

export default Settings;
